import React from "react";
import { useParams } from "react-router-dom";
import {
  CalendarClock,
  User,
  MapPin,
  FileCheck,
  UserRound,
  Mail,
  Phone,
  Home,
  FileText,
  List,
  Star,
  CreditCard,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@shared/ui/avatar";
import { useConsumerDetails } from "../../hooks";
import {formatTerritoryAddress} from '@shared/utils/address';
export type ApplicationStatus =
  | "pending_review"
  | "document_verification"
  | "site_inspection"
  | "approval"
  | "approved"
  | "installation"
  | "accepted"
  | "approval_inprogress"
  | "survey_inprogress"
  | "survey_completed"
  | "installation_inprogress"
  | "installation_completed"
  | "activation"
  | "completed"
  | "rejected";

interface ApplicationHeaderProps {
  remote_utility_id: any;
  is_detail_info: any;
  daysRemaining?: number;
  totalDays?: number;
}

const getStatusBadge = (status: ApplicationStatus) => {
  switch (status) {
    case "pending_review":
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-700 border-blue-200"
        >
          Pending Review
        </Badge>
      );
    case "document_verification":
      return (
        <Badge
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200"
        >
          Document Verification
        </Badge>
      );
    case "site_inspection":
      return (
        <Badge
          variant="outline"
          className="bg-orange-50 text-orange-700 border-orange-200"
        >
          Site Inspection
        </Badge>
      );
    case "approval":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200"
        >
         Approved
        </Badge>
      );
    case "approved":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200"
        >
         Approved
        </Badge>
      );
    case "installation":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Scheduled
        </Badge>
      );
    case "accepted":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Accepted
        </Badge>
      );
    case "approval_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Approval Inprogress
        </Badge>
      );
    case "survey_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Survey Inprogress
        </Badge>
      );
    case "survey_completed":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Survey Completed
        </Badge>
      );
    case "installation_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Inprogress
        </Badge>
      );
    case "installation_completed":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Completed
        </Badge>
      );
    case "completed":
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          Completed
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200"
        >
          Rejected
        </Badge>
      );
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

// Helper function to map API status to our ApplicationStatus
const mapApiStatusToApplicationStatus = (
  statusDisplay: string
): ApplicationStatus => {
  switch (statusDisplay?.toUpperCase()) {
    case "APPROVED":
      return "approved";
    case "ACCEPTED":
      return "accepted";
    case "PENDING":
      return "pending_review";
    case "REJECTED":
      return "rejected";
    case "COMPLETED":
      return "completed";
    case "ACTIVATED":
      return "activation";
    case "APPROVAL INPROGRESS":
      return "approval_inprogress";
    case "SURVEY INPROGRESS":
      return "survey_inprogress";
    case "SURVEY COMPLETED":
      return "survey_completed";
    case "INSTALLATION INPROGRESS":
      return "installation_inprogress";
    case "INSTALLATION COMPLETED":
      return "installation_completed";
    default:
      return "pending_review";
  }
};

const ApplicationHeader = ({
  remote_utility_id,
  is_detail_info,
  daysRemaining = 0,
  totalDays = 0,
}: ApplicationHeaderProps) => {
  // Get consumer_id from URL parameters
  const { consumer_id, consumerId, id } = useParams<{
    consumer_id?: string;
    consumerId?: string;
    id?: string;
  }>();

  // Use the first available parameter (handles different URL patterns)
  const consumerIdFromUrl = consumer_id || consumerId || id;

  const {
    data: consumerData,
    isLoading,
    error,
  } = useConsumerDetails({
    remote_utility_id,
    consumer_id: consumerIdFromUrl || "",
    is_detail_info: true,
  });

  // Show error if no consumer ID is found in URL
  if (!consumerIdFromUrl) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-red-600">Error: Consumer ID not found in URL</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-red-600">
          Error loading consumer details: {error.message}
        </div>
      </div>
    );
  }

  if (!consumerData?.result) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-gray-600">No consumer data available</div>
      </div>
    );
  }

  const consumer = consumerData.result;
  console.log('debug consumer,', consumer);
  // Map API data to component props - keeping original prop names
  const customerName = `${consumer.firstName} ${consumer.lastName}`;
  const customerEmail = consumer.email;
  const customerPhone = consumer.contactNumber;
  const applicationId = consumer.consumerNo;
  const submissionDate = consumer.createdDate;

  // Combine all service address data - prioritize address_data, fallback to territory_data
  const serviceAddressData = consumer.addressData?.service;
  const serviceTerritoryData = consumer.territoryData?.service;
  console.log('debug addresss', serviceAddressData);
const serviceAddress = formatTerritoryAddress(consumer.territoryData, "service") || "No address configured";
const billingAddress = formatTerritoryAddress(consumer.territoryData, "billing") || "No address configured";

    console.log('debug serviceAddress', serviceAddressData?.region);

  // Combine all billing address data - prioritize address_data, fallback to territory_data
  const billingAddressData = consumer.addressData?.billing;
  const billingTerritoryData = consumer.territoryData?.billing;


  const plan = consumer.planDetails?.planName;
  const category = consumer.categoryDisplay;
  const subCategory = consumer.subCategoryDisplay;
  const status = mapApiStatusToApplicationStatus(consumer.statusDisplay);
  const createdBy = consumer.createdUserRemoteName || "";
  const updatedOn = consumer.lastModifiedDate;
  const lastUpdatedBy = consumer.lastModifiedUserRemoteName || "";

  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 border-2 border-workflow-blue">
            <AvatarImage src="/placeholder.svg" alt={customerName} />
            <AvatarFallback>{customerName.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              {customerName}
              {getStatusBadge(status)}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Service Connection Request
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-md">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">SLA Status</p>
            <p className="text-sm font-medium flex items-center justify-center">
              <span>{consumer.slaInfo?.slaStatus || "N/A"}</span>
            </p>
          </div>
          <div className="text-center border-x border-gray-200 px-2">
            <p className="text-sm text-muted-foreground">SLA Due Date</p>
            <p className="text-sm font-medium">{consumer.slaInfo?.slaDueDate || "N/A"}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Time Remaining</p>
            <p className="text-sm font-medium">{consumer.slaInfo?.slaTimeRemaining || "N/A"}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Contact Information Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">
              Contact Information
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <div className="text-sm">{customerEmail || "N/A"}</div>
              </div>

              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-blue-600" />
                <div className="text-sm">{customerPhone || "N/A"}</div>
              </div>

              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Service Address:</div>
                  <div className="text-sm">{serviceAddress}</div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Home className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Billing Address:</div>
                  <div className="text-sm">{billingAddress}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Information Card */}
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold text-green-700 mb-3">
              Account Information
            </h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-green-700">
                  Consumer Number
                </div>
                <div className="text-sm text-right">{applicationId}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-green-700">
                  Category
                </div>
                <div className="text-sm text-right">{category}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-green-700">
                  Sub Category
                </div>
                <div className="text-sm text-right">{subCategory}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-green-700">
                  Connection Date
                </div>
                <div className="text-sm text-right">{submissionDate}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-green-700">Plan</div>
                <div className="text-sm text-right">{plan}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Application Details Card */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold text-amber-700 mb-3">
              Application Details
            </h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-amber-700">
                  Created On
                </div>
                <div className="text-sm text-right">{submissionDate}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-amber-700">
                  Created By
                </div>
                <div className="text-sm text-right">{createdBy}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-amber-700">
                  Updated On
                </div>
                <div className="text-sm text-right">{updatedOn}</div>
              </div>

              <div className="grid grid-cols-2">
                <div className="text-sm font-medium text-amber-700">
                  Last Updated By
                </div>
                <div className="text-sm text-right">{lastUpdatedBy}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationHeader;
